// In-memory offline state for the admin (this tab only):
// - network failures in a row of the polled reads (POLLED_ENDPOINTS: a
//   request that never got an answer; an aborted one is not counted). The
//   Offline banner shows, and writes are held, when the browser says
//   offline or after two such failures in a row (offlineByRule).
// - which reads each page shows, and when their saved copies were written
//   (pathname -> key -> savedAt), and which reads are being served from the
//   saved copy right now. The "Offline, showing saved data from" line reads it.
// - a blocked write (offline): listeners show the toast.

let failures = 0;
let version = 0;
const pathEntries = new Map();
const served = new Set();
const listeners = new Set();
const writeListeners = new Set();

const emit = () => {
  version += 1;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      // keep going
    }
  });
};

// The reads that poll in the background; only their failures count, so a
// burst of reads failing at once (a refresh) is not mistaken for two polls.
export const POLLED_ENDPOINTS = new Set(["getAdminSummary", "getPortalInbox"]);
export const FAILURES_FOR_OFFLINE = 2;

export const OFFLINE_WRITE_TEXT = "You are offline, nothing was saved";

export function browserOffline() {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch (e) {
    return false;
  }
}

export const offlineByRule = () => browserOffline() || failures >= FAILURES_FOR_OFFLINE;

export const cleanPath = (p) => {
  const s = String(p ?? "").split(/[?#]/)[0].replace(/\/+$/, "");
  return s || "/";
};

export const currentPath = () => {
  try {
    return cleanPath(window.location.pathname);
  } catch (e) {
    return "/";
  }
};

export const subscribeOffline = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
export const offlineVersion = () => version;
export const networkFailures = () => failures;

export function noteNetworkFailure() {
  failures += 1;
  emit();
}

export function noteReachable() {
  if (failures === 0) return;
  failures = 0;
  emit();
}

function setEntry(path, key, savedAt) {
  const p = cleanPath(path);
  if (!pathEntries.has(p)) pathEntries.set(p, new Map());
  pathEntries.get(p).set(key, savedAt);
}

// A live read just overwrote its saved copy.
export function noteLive(key, path, savedAt) {
  if (!key) return;
  pathEntries.forEach((m) => {
    if (m.has(key)) m.set(key, savedAt);
  });
  setEntry(path, key, savedAt);
  served.delete(key);
  emit();
}

// A read failed offline and its saved copy was served instead.
export function noteServed(key, path, savedAt) {
  if (!key) return;
  setEntry(path, key, savedAt);
  served.add(key);
  emit();
}

// The savedAt to show on this page, or 0: while offline (or while a read on
// this page is still the saved copy), the oldest saved copy the page shows.
export function savedLineFor(path, offline) {
  const m = pathEntries.get(cleanPath(path));
  if (!m || m.size === 0) return 0;
  const keys = Array.from(m.keys());
  const servedHere = keys.filter((k) => served.has(k));
  if (!offline && servedHere.length === 0) return 0;
  const times = (offline ? keys : servedHere).map((k) => Number(m.get(k)) || 0).filter((n) => n > 0);
  return times.length ? Math.min(...times) : 0;
}

export function onWriteBlocked(fn) {
  writeListeners.add(fn);
  return () => writeListeners.delete(fn);
}

export function noteWriteBlocked() {
  writeListeners.forEach((fn) => {
    try {
      fn(OFFLINE_WRITE_TEXT);
    } catch (e) {
      // keep going
    }
  });
}

// Tests only.
export function resetOfflineState() {
  failures = 0;
  version = 0;
  pathEntries.clear();
  served.clear();
  listeners.clear();
  writeListeners.clear();
}
